import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Divider,
  Link,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import styles from 'pages/main/CatalogDetailPage/CatalogDetailPage.module.css';
import default_theme from 'assets/themes/themes';
import { fetchCatalogDetailRequest } from '../../redux/catalog/catalogSlice';

const SourceDetailModal = ({ open, onClose, source,onAttach }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const catalogState = useSelector((state) => state.catalog);
  const catalogData = source?.entry_id ? catalogState.catalogDetails[String(source.entry_id)] : null;
  const loading = catalogState.loading;
  const error = catalogState.error;
  
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (open && source?.entry_id) {
      dispatch(fetchCatalogDetailRequest({ entryId: source.entry_id }));
      setSelectedTab(0); 
    }
  }, [open, source?.entry_id, dispatch]);

  const handleClose = () => {
    // catalogData and error are derived from Redux state, no need to set them
    onClose();
  };

  const handleViewMore = () => {
    if (source?.entry_id) {
      const backPath = encodeURIComponent("/wizard");
      
      const backLabel = encodeURIComponent("Back to Wizard");
      
      window.open(
        `/solutions/rural-development/solutions-catalog/${source.entry_id}?backPath=${backPath}&backLabel=${backLabel}`, 
        '_blank'
      );
      
      handleClose();
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  if (!open) return null;

  const renderField = (label, value) => {
    if (!value || value === 'None' || value === '') return null;
    return (
      <Typography variant="body2" className={styles.fieldStyle}>
        <strong>{label}:</strong> {value}
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: default_theme.palette.primary.white, borderBottom: `2px solid ${default_theme.palette.other.main}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: default_theme.palette.other.textDark, pr: 2 }}>
            {(catalogData?.combinedData || catalogData)?.solution_title || (catalogData?.combinedData || catalogData)?.title_english || (catalogData?.combinedData || catalogData)?.title || source?.solution_title || source?.title || 'Source Details'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: default_theme.palette.other.main,
              '&:hover': { bgcolor: default_theme.palette.other.tealHover }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: default_theme.palette.primary.white, p: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: default_theme.palette.other.main }} />
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" gutterBottom>
              Error loading source details: {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && catalogData && (() => {
          // API returns { entryId, combinedData: { aims, story, ... } }; content lives in combinedData (same as CatalogDetailPage)
          const data = catalogData.combinedData || catalogData;
          return (
          <Box>
            {/* Header Info */}
            <Box sx={{ mb: 3, p: 3, bgcolor: default_theme.palette.primary.main, borderRadius: 2, boxShadow: 2 }}>
              {(data.title_local || data.title) && (
                <Typography variant="h6" sx={{ fontStyle: 'italic', color: default_theme.palette.other.textSecondary, mb: 2 }}>
                  {data.title_local || data.title}
                </Typography>
              )}

              {/* Project Details Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: default_theme.palette.other.main, fontWeight: 600, mb: 2 }}>
                  Project Details
                </Typography>
                
                {/* Location */}
                {(data.location_town || data.location_region || data.location_country) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 0.5 }}>
                      Location
                    </Typography>
                    <Typography variant="body2">
                      {[data.location_town, data.location_region, data.location_country]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}

                {/* Organization */}
                {(data.lead_organization || data.lead_org_name) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 0.5 }}>
                      Organization
                    </Typography>
                    <Typography variant="body2">{data.lead_organization || data.lead_org_name}</Typography>
                    {(data.organisation_type || data.lead_org_type) && (
                      <Typography variant="caption" sx={{ color: default_theme.palette.other.textSecondary }}>
                        {data.organisation_type || data.lead_org_type}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Website */}
                {data.website && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 0.5 }}>
                      Website
                    </Typography>
                    <Link 
                      href={data.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        color: default_theme.palette.other.main,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Visit Project Website
                  </Link>
                  </Box>
                )}
              </Box>

              {/* RDD Categories */}
              {(data.rdd_1 || data.rdd_2 || data.rdd_3) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 1 }}>
                    Rural Development Drivers
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {data.rdd_1 && (
                      <Chip
                        label={data.rdd_1}
                        size="small"
                        sx={{ bgcolor: default_theme.palette.other.paleGreen, color: default_theme.palette.other.green }}
                      />
                    )}
                    {data.rdd_2 && (
                      <Chip
                        label={data.rdd_2}
                        size="small"
                        sx={{ bgcolor: default_theme.palette.other.paleGreen, color: default_theme.palette.other.green }}
                      />
                    )}
                    {data.rdd_3 && (
                      <Chip
                        label={data.rdd_3}
                        size="small"
                        sx={{ bgcolor: default_theme.palette.other.paleGreen, color: default_theme.palette.other.green }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Required Competencies */}
              {(data.required_competencies || data.competencies_required) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 0.5 }}>
                    Required Competencies
                  </Typography>
                  <Typography variant="body2">{data.required_competencies || data.competencies_required}</Typography>
                </Box>
              )}

              {/* Target Beneficiaries */}
              {(data.target_beneficiaries || data.beneficiaries) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: default_theme.palette.other.textSecondary, mb: 0.5 }}>
                    Target Beneficiaries
                  </Typography>
                  <Typography variant="body2">{data.target_beneficiaries || data.beneficiaries}</Typography>
                </Box>
              )}

              {source?.relevance && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`Relevance: ${(source.relevance * 100).toFixed(0)}%`}
                    sx={{ 
                      bgcolor: default_theme.palette.other.main, 
                      color: default_theme.palette.primary.main,
                      fontWeight: 600 
                    }}
                    size="small"
                  />
                </Box>
              )}
            </Box>

            {/* Content Sections - Tabbed Interface */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={selectedTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      minHeight: '56px',
                      bgcolor: default_theme.palette.other.white,
                      marginRight: 1,
                      borderRadius: '8px 8px 0 0',
                      gap: 1,
                      '& .MuiTab-iconWrapper': {
                        marginRight: '8px',
                        fontSize: '1.3rem',
                      },
                      '&.Mui-selected': {
                        bgcolor: default_theme.palette.other.main,
                        color: default_theme.palette.primary.main,
                      },
                      '&:not(.Mui-selected)': {
                        color: default_theme.palette.other.textDark,
                      }
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none'
                    }
                  }}
                >
                  <Tab 
                    icon={<GpsFixedIcon />} 
                    iconPosition="start"
                    label="Aims"
                  />
                  <Tab 
                    icon={<MenuBookIcon />} 
                    iconPosition="start"
                    label="Story"
                  />
                  <Tab 
                    icon={<TrendingUpIcon />} 
                    iconPosition="start"
                    label="Impacts"
                  />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ 
                bgcolor: default_theme.palette.primary.main, 
                borderRadius: '8px', 
                p: 3,
                minHeight: '200px',
                boxShadow: 1
              }}>
                {selectedTab === 0 && data.aims && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {data.aims}
                  </Typography>
                )}
                {selectedTab === 0 && !data.aims && (
                  <Typography variant="body2" sx={{ color: default_theme.palette.other.textSecondary, fontStyle: 'italic' }}>
                    No aims information available.
                  </Typography>
                )}
                
                {selectedTab === 1 && data.story && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {data.story}
                  </Typography>
                )}
                {selectedTab === 1 && !data.story && (
                  <Typography variant="body2" sx={{ color: default_theme.palette.other.textSecondary, fontStyle: 'italic' }}>
                    No story information available.
                  </Typography>
                )}
                
                {selectedTab === 2 && (data.impact || data.impacts) && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {data.impact || data.impacts}
                  </Typography>
                )}
                {selectedTab === 2 && !data.impact && !data.impacts && (
                  <Typography variant="body2" sx={{ color: default_theme.palette.other.textSecondary, fontStyle: 'italic' }}>
                    No impacts information available.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Additional Details */}
            {data.source && (
              <Box sx={{ mt: 3, p: 2, bgcolor: default_theme.palette.primary.main, borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: default_theme.palette.other.main }}>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {renderField('Source', data.source)}
              </Box>
            )}
          </Box>
          );
        })()}

        {!loading && !error && !catalogData && source && (
          <Box sx={{ p: 3, bgcolor: default_theme.palette.primary.main, borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              {source.summary || source.aim || 'No additional details available.'}
            </Typography>
            {source.url && (
              <Link 
                href={source.url} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ 
                  color: default_theme.palette.other.main,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                View Original Source →
              </Link>
            )}
          </Box>
        )}

        {/* View More Button */}
        {!loading && !error && source?.entry_id && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            {/* Attach Solution Button */}
            {onAttach && catalogData && (
              <Button
                variant="outlined"
                onClick={() => {
                  const data = catalogData.combinedData || catalogData;
                  onAttach({
                    id: source.entry_id,
                    title: data.solution_title || data.title_english || data.title || "Selected Solution"
                  });
                  onClose(); 
                }}
                sx={{
                  borderColor: default_theme.palette.other.main,
                  color: default_theme.palette.other.main,
                  fontWeight: 600,
                  px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '16px',
                  '&:hover': { bgcolor: 'rgba(19, 168, 158, 0.05)', borderColor: default_theme.palette.other.tealDark }
                }}
              >
                Attach to Challenge
              </Button>
            )}
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewMore}
              sx={{
                bgcolor: default_theme.palette.other.main,
                color: default_theme.palette.primary.main,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': {
                  bgcolor: default_theme.palette.other.tealDark,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${default_theme.palette.other.tealHover}`
                },
                transition: 'all 0.3s ease'
              }}
            >
              View Full Details
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SourceDetailModal;